import { useEffect, useRef } from 'react'
import { computeBarFrame } from './bar-frame'
import type { BarLayout } from './layout'
import { liveReadings } from '../../state/live-readings'
import type { GuitarString } from '../../music/tunings'
import { noteName, noteOctave } from '../../music/notation'
import { useTunerStore } from '../../state/tuner-store'

/** DOM handles for one bar, collected once when React mounts it. */
export interface BarElements {
  readonly root: HTMLElement
  readonly note: HTMLElement
  readonly octave: HTMLElement
  readonly needle: HTMLElement
  readonly cents: HTMLElement
  /** The `role="meter"` element, which has to carry the current value. */
  readonly track: HTMLElement
}

/** What the loop needs to know about each bar it draws. */
export interface BarBinding {
  /** Index of this string in the tuning, i.e. in the live readings. */
  readonly sourceIndex: number
  /** MIDI note the string is tuned to. */
  readonly targetMidi: number
}

/**
 * Per-bar state that must survive between frames.
 *
 * Every field that guards a DOM write starts at a value no real reading can
 * produce — empty strings, and `null` rather than `false` for the snap flag.
 * A fresh memory must force a rewrite of everything: it is created when a bar
 * changes which string it represents, and the node may still be showing the
 * previous string's values.
 */
interface BarMemory {
  cents: number | null
  midi: number | null
  zone: string
  note: string
  octave: string
  centsText: string
  snapped: boolean | null
  valueNow: string
}

const newMemory = (): BarMemory => ({
  cents: null,
  midi: null,
  zone: '',
  note: '',
  octave: '',
  centsText: '',
  snapped: null,
  valueNow: '',
})

/** Format the deviation the way it appears under the bar. */
function formatCents(cents: number | null): string {
  if (cents === null) return '–'
  const rounded = Math.round(cents)
  if (rounded === 0) return '0'
  return rounded > 0 ? `+${String(rounded)}` : String(rounded)
}

/**
 * Drive every bar from a single animation frame loop.
 *
 * The loop only ever writes to the DOM — no `getBoundingClientRect`, no
 * `offsetWidth` — so it cannot force a synchronous layout. Needle movement is
 * a `transform` on a full-width layer, which the compositor can handle without
 * touching layout at all. Text and class names are written only when they
 * actually change, which for a steady note is almost never.
 */
export function useBarRenderer(
  elementsRef: React.RefObject<(BarElements | null)[]>,
  bindings: readonly BarBinding[],
  layout: BarLayout,
): void {
  const memoryRef = useRef<BarMemory[]>([])
  const bindingsRef = useRef(bindings)
  const layoutRef = useRef(layout)

  // Keep the loop's view of the configuration current without restarting it.
  // Written in an effect rather than during render, so a discarded render pass
  // cannot leave the loop pointing at bindings that were never committed.
  //
  // The memory is indexed by bar position, so changing the layout or the
  // tuning re-points every slot at a different string. Dropping it forces the
  // next frame to rewrite every attribute; otherwise a stale value survives
  // whenever the incoming value happens to match the previous string's — a
  // bar could keep showing another string's snap marker indefinitely.
  useEffect(() => {
    bindingsRef.current = bindings
    layoutRef.current = layout
    memoryRef.current = []
  }, [bindings, layout])

  useEffect(() => {
    let handle = 0
    let previousTime: number | null = null

    const draw = (now: number): void => {
      handle = requestAnimationFrame(draw)

      const deltaMs = previousTime === null ? 16.67 : now - previousTime
      previousTime = now

      const currentBindings = bindingsRef.current
      const isColumns = layoutRef.current === 'columns'
      // Read live: changing the reference pitch, notation or transposition
      // must show up on the very next frame, with no reload.
      const { a4, notation, transposition } = useTunerStore.getState()
      const { strings } = liveReadings.snapshot()
      const elements = elementsRef.current
      const memories = memoryRef.current

      for (let i = 0; i < currentBindings.length; i += 1) {
        const binding = currentBindings[i]
        const element = elements[i]
        if (!binding || !element) continue

        const memory = (memories[i] ??= newMemory())
        const slot = strings[binding.sourceIndex]

        const frame = computeBarFrame({
          frequency: slot?.frequency ?? null,
          updatedAt: slot?.updatedAt ?? null,
          targetMidi: binding.targetMidi,
          a4,
          now: performance.now(),
          previousCents: memory.cents,
          previousMidi: memory.midi,
          deltaMs,
        })
        memory.cents = frame.cents
        memory.midi = frame.midi

        // Needle: a transform on a layer spanning the whole track, so the
        // offset is a share of the track rather than of the needle itself.
        const offset = frame.fraction * 50
        element.needle.style.transform = isColumns
          ? `translateY(${String(-offset)}%)`
          : `translateX(${String(offset)}%)`

        const zone = frame.fade === 'idle' ? 'idle' : frame.zone
        if (memory.zone !== zone) {
          memory.zone = zone
          element.root.dataset.zone = zone
        }

        const glow = frame.fade === 'live' ? '1' : frame.glow.toFixed(2)
        element.root.style.setProperty('--glow', glow)

        const shownMidi = frame.midi ?? binding.targetMidi
        const noteText = noteName(shownMidi, notation, transposition)
        if (memory.note !== noteText) {
          memory.note = noteText
          element.note.textContent = noteText
        }

        const octaveText = String(noteOctave(shownMidi, transposition))
        if (memory.octave !== octaveText) {
          memory.octave = octaveText
          element.octave.textContent = octaveText
        }

        if (memory.snapped !== frame.isSnapped) {
          memory.snapped = frame.isSnapped
          element.root.dataset.snapped = frame.isSnapped ? 'true' : 'false'
        }

        const centsText = formatCents(frame.cents)
        if (memory.centsText !== centsText) {
          memory.centsText = centsText
          element.cents.textContent = centsText

          // A `role="meter"` without `aria-valuenow` is an incomplete widget:
          // assistive technology is told the scale and never the reading. The
          // spoken text says which, because a bare 0 would be indistinguishable
          // from a string that is perfectly in tune.
          const valueNow = frame.cents === null ? '0' : String(Math.round(frame.cents))
          element.track.setAttribute('aria-valuenow', valueNow)
          element.track.setAttribute(
            'aria-valuetext',
            frame.cents === null ? 'keine Messung' : `${centsText} Cent`,
          )
          memory.valueNow = valueNow
        }
      }
    }

    handle = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(handle)
    }
  }, [elementsRef])
}

/** Build the render bindings for a tuning in display order. */
export function bindingsFor(
  ordered: readonly GuitarString[],
  tuningStrings: readonly GuitarString[],
): readonly BarBinding[] {
  return ordered.map((string) => ({
    sourceIndex: tuningStrings.findIndex((candidate) => candidate.id === string.id),
    targetMidi: string.midi,
  }))
}
